import { assertNever } from '@sparkdotfi/common-universal'

import { MigrationInfo } from '../../types'
import { formatPercentage } from '@/domain/common/format'
import { assets } from '@/ui/assets'
import { Button } from '@/ui/atoms/button/Button'
import { ExternalLinkButton } from '@/ui/atoms/link-button/LinkButton'
import { Panel } from '@/ui/atoms/panel/Panel'
import { links } from '@/ui/constants/links'
import { testIds } from '@/ui/utils/testIds'

export function UpgradeSavingsBanner(props: MigrationInfo) {
  if (props.type === 'sdai-to-susds') {
    return <UpgradeSdaiToSusdsBanner {...props} />
  }

  if (props.type === 'susdc-to-spusdc') {
    return <UpgradeSusdcToSpusdcBanner {...props} />
  }

  assertNever(props)
}

function UpgradeSdaiToSusdsBanner({
  openSDaiToSUsdsUpgradeDialog,
  apyImprovement,
}: Extract<MigrationInfo, { type: 'sdai-to-susds' }>) {
  return (
    <Panel
      spacing="m"
      className="flex items-center gap-3 bg-primary sm:gap-8"
      data-testid={testIds.savings.upgradeSDaiBanner}
    >
      <img
        src={assets.savings.sdaiUpgradeBannerIcon}
        alt="savings-dai-upgrade-banner-icon"
        className="w-[72px] md:w-[110px]"
      />
      <div className="flex w-full flex-col justify-between gap-3 lg:flex-row lg:items-center lg:gap-8">
        <div className="flex flex-col gap-1">
          <div className="typography-label-2 sm:typography-heading-5 text-primary">
            Upgrade your DAI Savings <br className="md:hidden" />
            {apyImprovement ? (
              <span className="whitespace-nowrap">
                and get <span className="text-system-success-primary">{formatPercentage(apyImprovement)}</span> higher
                APY!
              </span>
            ) : (
              <span className="whitespace-nowrap">and unlock the full potential of Spark Savings.</span>
            )}
          </div>
          <div className="typography-body-4 hidden text-secondary md:block lg:max-w-[48ch]">
            Upgrade at any size with no slippage. The upgrade is optional, and you can continue using DAI Savings.
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 lg:min-w-64 lg:gap-4">
          <Button variant="primary" size="m" onClick={openSDaiToSUsdsUpgradeDialog}>
            Upgrade now
          </Button>
          <ExternalLinkButton to={links.docs.savings.upgradeSdai} variant="tertiary" size="m">
            Learn more
          </ExternalLinkButton>
        </div>
      </div>
    </Panel>
  )
}

function UpgradeSusdcToSpusdcBanner({
  openSusdcToSpusdcUpgradeDialog,
}: Extract<MigrationInfo, { type: 'susdc-to-spusdc' }>) {
  return (
    <Panel spacing="m" className="flex items-center gap-3 bg-primary sm:gap-8">
      <img
        src={assets.savings.susdcUpgradeBannerIcon}
        alt="savings-usdc-upgrade-banner-icon"
        className="w-[72px] md:w-[110px]"
      />
      <div className="flex w-full flex-col justify-between gap-3 lg:flex-row lg:items-center lg:gap-8">
        <div className="flex flex-col gap-1">
          <div className="typography-label-2 sm:typography-heading-5 text-primary">
            Upgrade to Savings USDC V2 <br className="md:hidden" />
            to earn more{' '}
            <span className="whitespace-nowrap bg-gradient-spark-primary-1 bg-clip-text text-transparent">
              Spark Points
            </span>
          </div>
          <div className="typography-body-4 hidden text-secondary md:block lg:max-w-[38ch]">
            Switch to the new Savings USDC and maximize your earnings with additional Spark Points.
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 lg:min-w-64 lg:gap-4">
          <Button variant="primary" size="m" onClick={openSusdcToSpusdcUpgradeDialog}>
            Upgrade now
          </Button>
          <ExternalLinkButton to={links.docs.savings.earningSavings} variant="tertiary" size="m">
            Learn more
          </ExternalLinkButton>
        </div>
      </div>
    </Panel>
  )
}
